---
title: ecosystem.Ai
slug: ecosystem-ai
type: work
status: published
featured: true
summary: A real-time behavioral prediction platform — workbench, prediction server, and a Client Pulse Responder that scores while a person is still acting.
sitePublishedAt: "2026-09-18"
originalPublishedAt: "2018-09-01"
lastReviewedAt: "2026-10-03"
audiences:
  - business
  - product-developer
themes:
  - prediction-in-production
  - computational-social-science
  - network-analysis
sourceUrl: https://ecosystem.ai
originalAuthor: Jay van Zyl
sourceRelationship: founder-architect narrative of first-party product work
rightsStatus: first-party
seoDescription: Jay van Zyl founded ecosystem.Ai to put behavioral prediction into production with a workbench, prediction server, and real-time Pulse Responder.
canonicalPath: /work/ecosystem-ai
---

I built ecosystem.Ai so a bank, network operator, or retailer can score a person *while they are still acting*. It is a behavioral-intelligence framework: reusable algorithms, a runtime that learns in production, and a workbench that lets the people who own the outcome operate it.

The product is at [ecosystem.ai](https://ecosystem.ai). The operator manuals are at [developer.ecosystem.ai](https://developer.ecosystem.ai). This page is how I think about the architecture.

## Why a prediction platform

Computational social science is only useful if the model can intervene in the interaction. Dashboards explain yesterday. The Client Pulse Responder scores now, takes the response, and updates the next score.

Public origin language from the FinovateFall 2018 demo still holds:

> Computational social science solutions that predict future behaviour based on financial transactions and digital channel activity, in order to enable smart client interactions.

## The system

Four systems carry the platform. The production server, workbench, runtime, and data agents stay private. Public proof is linked where it exists.

1. **Workbench** — the operator surface. A team configures projects, data, models, and deployment here, including H2O scoring, without living inside the runtime. `Network` is a selectable dynamic-engagement learning approach inside a project: graph analysis of offers and interactions, not a routing product.
2. **Prediction server** — the control plane. Projects, deployments, the prediction-worker architecture, domain-catalog sync, and the model gateway the other apps call.
3. **Client Pulse Responder** — the scoring engine. It loads the campaign, runs pre-score, model, post-score, and reward plugins, and returns a ranked result while the person is still acting. The response is written back so dynamic engagement can keep learning. Operator detail is in the [runtime documentation](https://developer.ecosystem.ai/docs/runtime). The inspectable public sibling is [ecosystem-runtime-localbuild](https://github.com/ecogenetic/ecosystem-runtime-localbuild).
4. **Data agents** — explain live campaign and runtime data. Reports, quality, and statistics over what the platform already stored. Same sign-in as the workbench.

Notebooks hold Python algorithms and prediction stories, including the public [ecosystem-algorithms](https://github.com/ecogenetic/ecosystem-algorithms) set. The closed loop is the product: `POST /invocations` scores; `POST /response` records acceptance so the next interaction is not guessing.

Static models (H2O MOJO, XGBoost, GLM) sit beside dynamic algorithms: Ecosystem Rewards, Epsilon Greedy, Bayesian Probabilistic, Q-Learning, Loss Aversion, Risk Aversion, Prospect Theory, Sentimental Equilibrium, Coverage-Aware Thompson, Long-Tail Boost MF, **Network Analysis**, and generative models with fact injection.

Network Analysis models offers as a directed graph. Acceptance and co-presentation form weighted edges; a Personalized PageRank-style rank surfaces structurally central offers. [Read the method](/work/network-analysis) and the [developer documentation](https://developer.ecosystem.ai/docs/configuration/algorithms/networkanalysis).

## Public proof, with bounds

- FinovateFall 2018 — Client Pulse Responder and AutoML / runtime engine.
- Public SaaS launch at AI Africa Expo 2022.
- Public runtime architecture: [ecosystem-runtime-localbuild](https://github.com/ecogenetic/ecosystem-runtime-localbuild).
- Docs, changelog, ontology catalog, and Custom GPT helpers.

I do not republish client logos or marketing totals here. Those belong on the company site, and only with permission.

The earlier product — ecosystem graphs, company timelines, and the first prediction server — is archived as the [pre-pivot platform](/work/ecosystem-pre-pivot).

## Custom GPTs

I publish ChatGPT GPTs that walk a team from value proposition into runtime plugins. The book companion is [Built to Thrive in the World of AI](https://chatgpt.com/g/g-ZS1LEEzIv-built-to-thrive-in-the-world-of-ai). The product set is on [gpts.ecosystem.ai](https://gpts.ecosystem.ai).
